import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useContext, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { APIContext } from "../../Contexts/APIContext";
import { SessionContext } from "../../Contexts/SessionContext";
import { SiteReturnLabel } from "../../Site/SiteNavigation";
import ExportTripRateLineGraphDialog from "../../TripRate/Filters/Summary/ExportTripRateLineGraphDialog";
import ExportTripRateLineGraphViewModel, { ITripRateChartData } from "../../TripRate/ViewModels/ExportTripRateLineGraphViewModel";
import ModeSelectionViewModel from "../../TripRate/ViewModels/ModeSelectionViewModel";
import { useTripRateContext } from "../../TripRate/TripRateContext";
import { getFilteredSurveyRefs } from "../../TripRateNew/TriprateFilters";
import { getTripRateCalculationSurveyRefs, getValidActiveSurveyRefs } from "../../TripRate/TripRateSurveyRefs";
import TripRateSitesGraph, { TripRateSitesGraphHandle } from "./TripRateSitesGraph";
import TripRateSitesGraphModeSelection from "./TripRateSitesGraphModeSelection";
import { TripRateSitesGraphDirection, TripRateSitesGraphView } from "./TripRateSitesGraphTypes";

type TripRateSitesGraphOrigin = "surveySelection" | "tripRateCalculation" | "rankOrder";

interface IProps {
    open: boolean;
    onClose: () => void;
    origin: TripRateSitesGraphOrigin;
    returnLabel: SiteReturnLabel;
    returnTo: string;
    selectedMode: number | null;
    direction: TripRateSitesGraphDirection;
    view: TripRateSitesGraphView;
    allowModeSelection: boolean;
    availableModes?: ModeSelectionViewModel[];
    onSelectedModeChange: (mode: number | null) => void;
    onDirectionChange: (direction: TripRateSitesGraphDirection) => void;
    onViewChange: (view: TripRateSitesGraphView) => void;
}

const graphHeight = 640;

export default function TripRateSitesGraphDialog({
    open,
    onClose,
    origin,
    returnLabel,
    returnTo,
    selectedMode,
    direction,
    view,
    allowModeSelection,
    availableModes = [],
    onSelectedModeChange,
    onDirectionChange,
    onViewChange,
}: IProps) {
    const { postCSV, postPDF } = useContext(APIContext);
    const { sessionState } = useContext(SessionContext);
    const { surveys } = useTripRateContext();
    const location = useLocation();
    const graphRef = useRef<TripRateSitesGraphHandle>(null);
    const [chartData, setChartData] = useState<ITripRateChartData | null>(null);
    const [exportOpen, setExportOpen] = useState(false);
    const surveyRefs = useMemo(() => {
        const filteredSurveyIds = getFilteredSurveyRefs(surveys);
        if (origin === "tripRateCalculation") {
            return getTripRateCalculationSurveyRefs(location.search, sessionState.selectedSitesAndSurveys, filteredSurveyIds);
        }

        return getValidActiveSurveyRefs(sessionState.selectedSitesAndSurveys, filteredSurveyIds);
    }, [location.search, origin, surveys, sessionState.selectedSitesAndSurveys]);
    const headerIndex = sessionState.primaryFilters.headerIndex;
    const selectedModeLabel = availableModes.find(mode => mode.modeId === selectedMode)?.modeName;

    const title = useMemo(() => {
        if (!allowModeSelection) {
            return "Selected Sites Trip Rate Graph";
        }

        if (view === "selection") {
            return "Select Site Trip Rate Mode";
        }

        return selectedModeLabel
            ? `Selected Sites Trip Rate Graph - ${selectedModeLabel}`
            : "Selected Sites Trip Rate Graph";
    }, [allowModeSelection, selectedModeLabel, view]);

    const handleExport = (vm: ExportTripRateLineGraphViewModel) => {
        if (vm.exportType === "JPEG") {
            graphRef.current?.exportToJPEG(vm.fileName || "Trip Rate Line Graph");
            setExportOpen(false);
            return;
        }

        if (!chartData) {
            setExportOpen(false);
            return;
        }

        vm.chartData = chartData;
        vm.mainUse = sessionState.tripRateMain;
        vm.subUse = sessionState.tripRateSub;
        vm.selectedParam = sessionState.primaryFilters.headerIndex;

        if (vm.exportType === "CSV") {
            postCSV("CsvGenerator", "GetTripRateLineGraphExport", vm.fileName || "Trip Rate Line Graph", vm);
        } else {
            postPDF("PDFGenerator", "GetTripRateLineGraphExport", vm.fileName || "Trip Rate Line Graph", vm.displayToScreen, vm);
        }

        setExportOpen(false);
    };

    const isGraphView = view === "graph" || !allowModeSelection;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle>
                <Stack spacing={0.5}>
                    <Typography variant="h5">{title}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {isGraphView ? (
                    <TripRateSitesGraph
                        ref={graphRef}
                        surveyRefs={surveyRefs}
                        headerIndex={headerIndex}
                        vehicleType={selectedMode ?? 0}
                        vehicleTypeLabel={selectedModeLabel}
                        direction={direction}
                        origin={origin}
                        returnLabel={returnLabel}
                        returnTo={returnTo}
                        onChartDataReady={setChartData}
                        onDirectionChange={onDirectionChange}
                        height={graphHeight}
                    />
                ) : (
                    <TripRateSitesGraphModeSelection
                        modes={availableModes}
                        selectedMode={selectedMode}
                        onSelectMode={onSelectedModeChange}
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                {!isGraphView && (
                    <Button
                        fullWidth={false}
                        onClick={() => onViewChange("graph")}
                        disabled={selectedMode === null}
                    >
                        Display Graph
                    </Button>
                )}
                {isGraphView && allowModeSelection && (
                    <Button fullWidth={false} onClick={() => onViewChange("selection")}>Change Mode</Button>
                )}
                {isGraphView && (
                    <Button fullWidth={false} onClick={() => setExportOpen(true)} disabled={!chartData}>
                        Export
                    </Button>
                )}
                <Button fullWidth={false} onClick={onClose}>Close</Button>
            </DialogActions>
            {exportOpen && (
                <ExportTripRateLineGraphDialog
                    dialogOpen={exportOpen}
                    handleClose={() => setExportOpen(false)}
                    exportFiltersViewModel={handleExport}
                />
            )}
        </Dialog>
    );
}
